import React from "react"
import {useCookies} from "react-cookie"
import {addDays} from "date-fns"
import {formatWithOptions} from "date-fns/fp"
import {ja, enUS} from "date-fns/locale"

const COOKIE_DOMAIN = process.env.COOKIE_DOMAIN
const COOKIE_EXPIRES = process.env.COOKIE_EXPIRES
const DASHBOARD_URL = process.env.DASHBOARD_URL
const BACK_URL = process.env.BACK_URL

export const upperCamel = (target) => target.charAt(0).toUpperCase() + target.slice(1)
export const getAppEnv = () => process.env.APP_ENV ?? `demo`
export const isDevelop = () => [`localhost`, `develop`, `development`].includes(getAppEnv())
export const isAuth0Stripe = () => !!process.env.AUTH0_STRIPE
export const isAmOne = () => !!process.env.AMONE

/** シングルユーザモードか判定する */
export const isSingleUser = () => isAmOne() || !process.env.AUTH0_STRIPE

const getCookieName = (type) => {
  if (isSingleUser())
    return `single/milize${type}`
  if (getAppEnv() === `development`)
    return`develop/milize${type}`

  return `${getAppEnv()}/milize${type}`
}

/** 認証トークンCookieキーを返す */
export const getTokenCookieName = () => getCookieName(`Token`)

/** 認証ユーザ情報Cookieキーを返す */
export const getUserCookieName = () => getCookieName(`User`)

/** シングルユーザ認証Cookie */
export const getSingleToken = () => isAmOne() ? process.env.AMONE_TOKEN : `MilizeAIDemo`

/** 認証Cookieを取得 */
export const getTokenCookie = (cookies) => {
  if (!cookies) return null
  if (cookies[getTokenCookieName()]) return cookies[getTokenCookieName()]
  return cookies.milizeToken ?? null
}

/** 認証ーザ情報Cookieを取得 */
export const getUserCookie = (cookies) => {
  if (!cookies) return null
  if (cookies[getUserCookieName()]) return cookies[getUserCookieName()]
  return null
}

/** 認証Cookieを取得 */
export const getAuthToken = () => {
  const [cookies, setCookie] = useCookies([ getTokenCookieName() ])
  return getTokenCookie(cookies)
}

/** ダッシュボードに戻る */
export const redirectToDashboard = async (withBackUrl = false) => {
  if (isAmOne()) return
  if (isAuth0Stripe()) {
    if (withBackUrl) {
      window.location = `${DASHBOARD_URL}/?${BACK_URL}`
    } else {
      window.location = DASHBOARD_URL
    }
  } else {
    window.location = DASHBOARD_URL
  }
  await sleep(5000)
}

export const sleep = (time) => new Promise((resolve) => setTimeout(resolve, time))
export const nl2br = (str) =>
  typeof str === `string` ? str.split(/([\n\r])/g).map((line) => (line.match(/([\n\r])/g) ? <br /> : line)) : ``
export const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`)
export const getPrefixRegExp = (value) => new RegExp(`^` + escapeRegExp(value))
export const getCookieOptions = (override = {}) => {
  return {
    ...{
      path: `/`,
      domain: COOKIE_DOMAIN,
      secure: getAppEnv() === `production`,
      expires: addDays(new Date(), Number(COOKIE_EXPIRES)),
    },
    override,
  }
}

export const returnToTop = () => {
  const x = window.scrollX
  window.scrollTo(x, 0)
}

export const toBoolean = (data) => {
  if (!data) return false
  return data.toLowerCase() === `true`
}

/** 初期値を使用して1次元配列を生成する(定数のみ) */
export const arrayInit1D = (xSize, initValue) => {
  return new Array(xSize).fill(initValue)
}

/** 初期値を使用して2次元配列を生成する(定数のみ) */
export const arrayInit2D = (ySize, xSize, initValue) => {
  let table = new Array(xSize)
  // eslint-disable-next-line no-unused-vars
  let _ = [...Array(ySize)].map((_, y) => (table[y] = new Array(xSize).fill(initValue)))
  return table
}

/** 初期値を使用して3次元配列を生成する(定数のみ) */
export const arrayInit3D = (zSize, ySize, xSize, initValue) => {
  let table = new Array(xSize)
  // eslint-disable-next-line no-unused-vars
  let loop1 = [...Array(zSize)].map((_, z) => {
    let arr = new Array(ySize)
    // eslint-disable-next-line no-unused-vars
    let loop2 = [...Array(ySize)].map((_, y) => {
      arr[y] = new Array(xSize).fill(initValue)
    })
    table[z] = arr
  })
  return table
}

export const convertDate = (date, locale) => {
  let dateToString = null
  if (locale === `ja`)
    dateToString = formatWithOptions(
      {
        locale: ja,
      },
      `yyyy-MM-dd`,
    )
  else
    dateToString = formatWithOptions(
      {
        locale: enUS,
      },
      `yyyy-MM-dd`,
    )
  const toUpper = (arg) => String(arg).toUpperCase()
  return toUpper(dateToString(date))
}

export const sortList = (dataList, flag = true) => {
  dataList.sort((a, b) => {
    if (flag) {
      return a > b ? 1 : -1
    }
    return a < b ? 1 : -1
  })
  return dataList
}

export const PromiseSingleHandler = {
  parallel: async (iterable, callback) => {
    let array = []
    // eslint-disable-next-line no-unused-vars
    for (let v of iterable) {
      array.push(callback(v))
    }
    try {
      return await Promise.all(array)
    } catch (error) {
      throw new Error()
    }
  },
  serial: async (iterable, callback) => {
    let array = []
    // eslint-disable-next-line no-unused-vars
    for (let v of iterable) {
      array.push(await callback(v))
    }
    return array
  },
  immediate: async (iterable, callback) => {
    // eslint-disable-next-line no-unused-vars
    for (let v of iterable) {
      callback(v)
    }
  },
}

export const getQueries = () =>
  Object.fromEntries(
    window.location.search
      .slice(1)
      .split(`&`)
      .filter((item) => item)
      .map((item) => item.split(`=`)),
  )
export const getQuery = (key) => getQueries()[key]

export const weekendJudge = (data) => {
  if (!data) return true
  const begin = data.slice(0, 1)
  const end = data.slice(-1)
  if (begin === `D` && end === `N`) return true
  return false
}

/**
 * 時系列データを標準化する。
 * @param data 時系列データ(APIで取得したもの)
 * @param ranking ランキング順位(グラフ表示識別用)
 * @description 基準日価格を0となるように-1～1で標準化する
 */
export const convertChartNorm = (data, ranking) => {
  const {backward, forward = null} = data[0]

  const baseValues = backward.slice(-1)[0] // 基準日価格
  //const keys = Object.keys(baseValues).filter((v) => v !== `date` && v !== `movements`) // 価格キー
  const keys = [ `open`, `close`, `high`, `low`, `volume`, `middle` ]

  // 基準日以前最大幅
  const maxValues = { }
  keys.map((k) => {
    const a = backward.map((r) => {
      return Math.abs(r[k] - baseValues[k])
    })
    maxValues[k] = Math.max(...a)
  })
  
  // 標準化処理
  const normalize = (values)=> {
    let charts = { }
    keys.map((k) => {
      charts[k] = values.map((r) => {
        const v = r[k] ? (r[k] - baseValues[k]) / maxValues[k] : 0
        return {
          ranking,
          date: r.date,
          [k]: v,
        }
      })
    })
    return charts
  }

  return {
    backwardNorm: normalize(backward),
    forwardNorm: normalize(forward),
  }
}

// OCHL全体で正規化
export const convertChartNormOld = (data, ranking) => {
  // norm =  (A - min) / (max-min)
  const {backward, forward = null} = data[0]
  let allData = backward
  //  let nonZeroData = backward.filter((data) => (mainGroup.slice(0,2) === `FX`) ? data.middle : data.close)

  const stockMin = allData.reduce((a, b) => (a.low < b.low ? a : b))
  const stockMax = allData.reduce((a, b) => (a.high > b.high ? a : b))
  const volumeMin = allData.reduce((a, b) => (a.volume < b.volume ? a : b))
  const volumeMax = allData.reduce((a, b) => (a.volume > b.volume ? a : b))
  const fxMin = allData.reduce((a, b) => (a.middle < b.middle ? a : b))
  const fxMax = allData.reduce((a, b) => (a.middle > b.middle ? a : b))

  const stockAbs = Math.abs(stockMax.high - stockMin.low)
  const volumeAbs = Math.abs(volumeMax.volume - volumeMin.volume)
  const fxAbs = Math.abs(fxMax.middle - fxMin.middle)

  const normalize = (target, targetMin, targetAbs) => {
    return allData.map((data) => {
      const value = data[target] ? (data[target] - targetMin) / targetAbs : 0
      return {
        ranking,
        date: data.date,
        [target]: value,
      }
    })
  }

  const open = normalize(`open`, stockMin.low, stockAbs)
  const high = normalize(`high`, stockMin.low, stockAbs)
  const low = normalize(`low`, stockMin.low, stockAbs)
  const close = normalize(`close`, stockMin.low, stockAbs)
  const volume = normalize(`volume`, volumeMin.volume, volumeAbs)
  const middle = normalize(`middle`, fxMin.middle, fxAbs)

  allData = forward

  const openF = normalize(`open`, stockMin.low, stockAbs)
  const highF = normalize(`high`, stockMin.low, stockAbs)
  const lowF = normalize(`low`, stockMin.low, stockAbs)
  const closeF = normalize(`close`, stockMin.low, stockAbs)
  const volumeF = normalize(`volume`, volumeMin.volume, volumeAbs)
  const middleF = normalize(`middle`, fxMin.middle, fxAbs)

  const backwardNorm = {
    open,
    high,
    low,
    close,
    volume,
    middle,
  }
  const forwardNorm = {
    open: openF,
    high: highF,
    low: lowF,
    close: closeF,
    volume: volumeF,
    middle: middleF,
  }

  return {
    backwardNorm,
    forwardNorm,
  }
}

// OCHLそれぞれで正規化
export const convertChartNorm2 = (data, ranking) => {
  // norm =  (A - min) / (max-min)
  const {backward, forward = null} = data[0]
  let allData = backward

  const normalize = (target) => {
    const min = allData.reduce((a, b) => (a[target] < b[target] ? a : b))
    const max = allData.reduce((a, b) => (a[target] > b[target] ? a : b))
    const abs = Math.abs(max[target] - min[target])
    return allData.map((data) => {
      const value = data[target] ? (data[target] - min[target]) / abs : 0
      return {
        ranking,
        date: data.date,
        [target]: value,
      }
    })
  }

  const open = normalize(`open`)
  const high = normalize(`high`)
  const low = normalize(`low`)
  const close = normalize(`close`)
  const volume = normalize(`volume`)
  const middle = normalize(`middle`)

  allData = forward

  const openF = normalize(`open`)
  const highF = normalize(`high`)
  const lowF = normalize(`low`)
  const closeF = normalize(`close`)
  const volumeF = normalize(`volume`)
  const middleF = normalize(`middle`)

  const backwardNorm = {
    open,
    high,
    low,
    close,
    volume,
    middle,
  }
  const forwardNorm = {
    open: openF,
    high: highF,
    low: lowF,
    close: closeF,
    volume: volumeF,
    middle: middleF,
  }
  return {
    backwardNorm,
    forwardNorm,
  }
}
